import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SearchRounded, PersonRounded, PsychologyRounded, EventNoteRounded, NorthEastRounded, BoltRounded } from "@mui/icons-material";
import { fetchById } from "../helpers/actions";
import { adminSearchUrl } from "../helpers/urls";

/* Ctrl+K / ⌘K: jump to any page the admin can open, run a quick action, or find a client / therapist (name,
   email, phone) or a booking (id). `pages` and `actions` come from the layout, already filtered by role. */
export default function CommandPalette({ open, onClose, pages, actions }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [found, setFound] = useState({ clients: [], therapists: [], bookings: [] });
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setQ(""); setFound({ clients: [], therapists: [], bookings: [] }); setSel(0);
    setTimeout(() => inputRef.current?.focus(), 30);
  }, [open]);

  // people / bookings from the API, a moment after typing stops
  useEffect(() => {
    if (!open) return undefined;
    const text = q.trim();
    if (text.length < 2) { setFound({ clients: [], therapists: [], bookings: [] }); return undefined; }
    let alive = true;
    setBusy(true);
    const t = setTimeout(() => {
      fetchById(adminSearchUrl, { q: text })
        .then((r) => { if (alive && r?.status) setFound(r.data); })
        .catch(() => {})
        .finally(() => alive && setBusy(false));
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [q, open]);

  const rows = useMemo(() => {
    const text = q.trim().toLowerCase();
    const match = (s) => !text || String(s).toLowerCase().includes(text);
    const pageRows = pages.filter((p) => match(p.label) || match(p.hub || "") || match(p.path)).slice(0, text ? 8 : 12)
      .map((p) => ({ kind: "page", key: `p${p.path}`, title: p.label, sub: p.hub || "", to: p.path, icon: p.icon }));
    const actionRows = actions.filter((a) => match(a.label)).slice(0, text ? 4 : 6)
      .map((a) => ({ kind: "action", key: `a${a.to}`, title: a.label, sub: "Quick action", to: a.to, icon: <BoltRounded /> }));
    const people = [
      ...found.clients.map((c) => ({ kind: "client", key: `c${c.id}`, title: c.name, sub: c.sub || "Client", to: c.to, icon: <PersonRounded /> })),
      ...found.therapists.map((c) => ({ kind: "therapist", key: `t${c.id}`, title: c.name, sub: c.sub || "Therapist", to: c.to, icon: <PsychologyRounded /> })),
      ...found.bookings.map((c) => ({ kind: "booking", key: `b${c.id}`, title: c.name, sub: c.sub, to: c.to, icon: <EventNoteRounded /> })),
    ];
    return text ? [...people, ...pageRows, ...actionRows] : [...actionRows, ...pageRows];
  }, [q, pages, actions, found]);

  useEffect(() => { setSel(0); }, [q]);
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-i="${sel}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [sel]);

  if (!open) return null;
  const go = (r) => { if (!r) return; onClose(); navigate(r.to); };
  const onKey = (e) => {
    if (e.key === "Escape") { e.preventDefault(); onClose(); }
    else if (e.key === "ArrowDown") { e.preventDefault(); setSel((i) => Math.min(rows.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSel((i) => Math.max(0, i - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); go(rows[sel]); }
  };
  const label = { page: "Page", action: "Action", client: "Client", therapist: "Therapist", booking: "Booking" };

  return (
    <div className="cp-bg" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <style>{CP_CSS}</style>
      <div className="cp" role="dialog" aria-modal="true" aria-label="Search">
        <div className="cp-in">
          <SearchRounded />
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey}
            placeholder="Search pages, clients, therapists, booking ID…" aria-label="Search" />
          {busy ? <span className="cp-busy" aria-hidden="true" /> : <kbd>Esc</kbd>}
        </div>
        <div className="cp-list" ref={listRef} role="listbox">
          {rows.length === 0 ? (
            <div className="cp-empty">{q.trim().length >= 2 && !busy ? "Nothing found. Try a name, phone number or page." : "Type to search…"}</div>
          ) : rows.map((r, i) => (
            <button key={r.key} type="button" data-i={i} role="option" aria-selected={i === sel}
              className={`cp-row${i === sel ? " on" : ""}`} onMouseEnter={() => setSel(i)} onClick={() => go(r)}>
              <span className={`cp-ic ${r.kind}`}>{r.icon}</span>
              <span className="cp-txt"><b>{r.title}</b>{r.sub && <small>{r.sub}</small>}</span>
              <em>{label[r.kind]}</em>
              <NorthEastRounded className="cp-go" />
            </button>
          ))}
        </div>
        <div className="cp-foot"><span><kbd>↑</kbd><kbd>↓</kbd> move</span><span><kbd>Enter</kbd> open</span><span><kbd>Ctrl</kbd><kbd>K</kbd> anywhere</span></div>
      </div>
    </div>
  );
}

const CP_CSS = `
.cp-bg { position: fixed; inset: 0; z-index: 100000; background: rgba(15,23,42,.45); display: flex; align-items: flex-start; justify-content: center; padding: 10vh 14px 14px; }
.cp { width: 100%; max-width: 620px; background: #fff; border-radius: 16px; box-shadow: 0 30px 80px -20px rgba(0,0,0,.45); overflow: hidden; display: flex; flex-direction: column; max-height: 76vh; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Inter, sans-serif; }
.cp-in { display: flex; align-items: center; gap: 10px; padding: 12px 14px; border-bottom: 1px solid #e2e8e4; }
.cp-in > svg { color: #1e7a4c; font-size: 22px !important; }
.cp-in input { flex: 1; min-width: 0; border: 0; outline: none; font-size: 16px; color: #0b1712; background: none; font-family: inherit; }
.cp kbd { font-family: inherit; font-size: 11px; font-weight: 700; color: #64748b; background: #f1f5f9; border: 1px solid #e2e8f0; border-bottom-width: 2px; border-radius: 6px; padding: 1px 6px; }
.cp-busy { width: 16px; height: 16px; border-radius: 50%; border: 2px solid #d1fae5; border-top-color: #1e7a4c; animation: cpSpin .7s linear infinite; }
@keyframes cpSpin { to { transform: rotate(360deg); } }
.cp-list { overflow-y: auto; padding: 6px; }
.cp-row { display: flex; align-items: center; gap: 11px; width: 100%; padding: 9px 10px; border: 0; background: none; border-radius: 10px; cursor: pointer; text-align: left; font-family: inherit; }
.cp-row.on { background: #f1f8f4; }
.cp-ic { width: 32px; height: 32px; border-radius: 9px; display: grid; place-items: center; background: #f1f5f9; color: #475569; flex-shrink: 0; }
.cp-ic svg { font-size: 18px !important; }
.cp-ic.client { background: #e0f2fe; color: #0369a1; } .cp-ic.therapist { background: #e7f6ec; color: #166534; } .cp-ic.action { background: #fef3c7; color: #b45309; } .cp-ic.booking { background: #ede9fe; color: #6d28d9; }
.cp-txt { flex: 1; min-width: 0; } .cp-txt b { display: block; font-size: 14px; color: #0b1712; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cp-txt small { display: block; font-size: 12px; color: #64748b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cp-row em { font-style: normal; font-size: 11px; font-weight: 700; color: #94a3b8; }
.cp-go { font-size: 16px !important; color: #cbd5e1; } .cp-row.on .cp-go { color: #1e7a4c; }
.cp-empty { padding: 22px; text-align: center; color: #64748b; font-size: 13.5px; }
.cp-foot { display: flex; gap: 14px; padding: 8px 14px; border-top: 1px solid #e2e8e4; font-size: 11.5px; color: #64748b; background: #fafbfa; }
.cp-foot span { display: inline-flex; align-items: center; gap: 4px; }
@media (max-width: 640px) { .cp-bg { padding-top: 12px; } .cp-foot { display: none; } }
`;
